import {useCallback, useEffect, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import {useTransition, animated} from 'react-spring';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import IconButton from '@mui/material/IconButton';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Notification from './Notification';
import {useAppSelector} from '../../../store/hooks';
import {NotificationProps} from '../../../constants/types';
import {deleteNotification, getNotifications} from '../../../api/notifications';
import Spinner from '../../common/Spinner';
import {useSnackbarHook} from '../../../hooks/useSnackbar';

const REFRESH_INTERVAL_IN_MILLISECONDS = 60000;
const DEBOUNCE_DELAY = 300;

const ANIMATION_RULES = {
  from: {opacity: 0, transform: 'translate3d(0, -25%, 0)', pointerEvents: 'none'},
  enter: {opacity: 1, transform: 'translate3d(0, 0, 0)', pointerEvents: 'auto'},
  leave: {opacity: 0, transform: 'translate3d(0, -25%, 0)', pointerEvents: 'none'}
};

const initialQuery = {
  page: 1,
  per_page: 10
};

type NotificationsResponseProps = {
  content: NotificationProps[],
  pageNumber: number,
  totalPages: number,
  totalElements: number
}

const Notifications = () => {
  const {activeTenant} = useAppSelector(({permissions}) => permissions);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [isLoading, setNotificationsLoading] = useState(false);
  const [loadedNotifications, setLoadedNotifications] =
    useState<NotificationProps[]>([]);
  const [pageNumber, setPageNumber] = useState(initialQuery.page);
  const [isDismissing, setIsDismissing] = useState(false);
  const {addErrorToast} = useSnackbarHook();

  const [open, setOpen] = useState(false);

  const closeDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const openDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const getNotificationsData = useCallback(async (
      page: number, shouldResetNotifications = false
  ) => {
    if (!activeTenant || isLoading) return;

    setNotificationsLoading(true);

    const requestParams = {
      page,
      per_page: initialQuery.per_page
    };

    try {
      const {
        pageNumber = 1, content = [], totalPages = 1
      }: NotificationsResponseProps =
        await getNotifications(requestParams) || {};

      const isFullyLoaded = pageNumber >= totalPages;

      setPageNumber(pageNumber);

      setLoadedNotifications((prevNotifications) => {
        if (shouldResetNotifications) {
          return content;
        }

        return [...prevNotifications, ...content];
      });

      setIsFullyLoaded(isFullyLoaded);
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setNotificationsLoading(false);
    }
  }, [isLoading, activeTenant]);

  useEffect(() => {
    const shouldResetNotifications = true;

    getNotificationsData(initialQuery.page, shouldResetNotifications);

    const interval = setInterval(() => {
      getNotificationsData(initialQuery.page, shouldResetNotifications);
    }, REFRESH_INTERVAL_IN_MILLISECONDS);

    return () => clearInterval(interval);
  }, [activeTenant]);

  const dismissNotification = useCallback(debounce(async (id: number) => {
    setIsDismissing(true);

    try {
      await deleteNotification(id);

      setLoadedNotifications((prevNotifications) => prevNotifications
          ?.filter((notification) => notification?.id !== id));
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setIsDismissing(false);
    }
  }, DEBOUNCE_DELAY), []);

  const notificationsList = useTransition(loadedNotifications,
      (item) => item.id,
      // @ts-ignore - need to prevent pointer-events error
      ANIMATION_RULES
  );

  return (
    <>
      <IconButton
        classes={{
          root: classNames('icon-button', {
            'notification-mark': loadedNotifications?.length,
            'active': open
          })
        }}
        onMouseDown={openDrawer}>
        <NotificationsIcon className="icon" />
      </IconButton>

      <SwipeableDrawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        onOpen={openDrawer}
        classes={{
          paper: 'notifications'
        }}
      >
        <div className="notifications-wrapper">
          <div className="notification-header">
            <p>Notifications</p>
            <button
              onClick={closeDrawer}
              className="cross">
              &#x2715;
            </button>
          </div>
          <div className="notifications-list">
            <InfiniteScroll
              loadMore={() => {
                getNotificationsData(pageNumber + 1);
              }}
              hasMore={!isFullyLoaded}
              useWindow={false}
              loader={<Spinner key={'notification_spinner'} />}
            >
              {notificationsList.map(({item, props, key}) => (
                item && (
                  <animated.div style={props} key={key}>
                    <Notification
                      key={`notification_${item?.id}`}
                      notification={item}
                      handleCloseNotifications={closeDrawer}
                      dismissNotification={() => dismissNotification(item?.id)}
                      isDismissing={isDismissing}
                    />
                  </animated.div>
                )
              ))}
            </InfiniteScroll>
          </div>
        </div>
      </SwipeableDrawer>
    </>
  );
};

export default Notifications;
